import React,{createContext,useContext,useReducer,useEffect} from 'react';
import {brandKitsApi} from '../lib/brandKitsApi';

const BrandKitContext=createContext();

const normalizeBrandKits = (arr) =>
  Array.isArray(arr)
    ? arr.map(kit => ({
        ...kit,
        updated_at: kit?.updated_at || null,
        created_at: kit?.created_at || null,
      }))
    : [];

const initialState={brandKits: [],currentBrandKit: null,loading: false,error: null,isOnline: navigator.onLine};

function brandKitReducer(state,action) {
  switch (action.type) {
    case 'SET_LOADING':
      return {...state,loading: action.payload};
    case 'SET_ERROR':
      return {...state,error: action.payload,loading: false};
    case 'SET_BRAND_KITS':
      return {...state,brandKits: action.payload,loading: false};
    case 'SET_CURRENT_BRAND_KIT':
      return {...state,currentBrandKit: action.payload};
    case 'ADD_BRAND_KIT':
      return {...state,brandKits: [...state.brandKits,action.payload],loading: false};
    case 'UPDATE_BRAND_KIT':
      return {
        ...state,
        brandKits: state.brandKits.map(kit=> 
          kit.id===action.payload.id ? action.payload : kit
        ),
        currentBrandKit: state.currentBrandKit?.id===action.payload.id ? action.payload : state.currentBrandKit,
        loading: false
      };
    case 'DELETE_BRAND_KIT':
      return {
        ...state,
        brandKits: state.brandKits.filter(kit=> kit.id !==action.payload),
        currentBrandKit: state.currentBrandKit?.id===action.payload ? null : state.currentBrandKit,
        loading: false
      };
    case 'SET_ONLINE':
      return {...state,isOnline: action.payload};
    default:
      return state;
  }
}

export function BrandKitProvider({children}) {
  const [state,dispatch]=useReducer(brandKitReducer,initialState);

  // Local storage fallback
  const getFromLocalStorage=(key)=> {
    try {
      const item=localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  };

  const saveToLocalStorage=(key,data)=> {
    try {
      localStorage.setItem(key,JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save to localStorage:',error);
    }
  };

  // Load brand kits
  const loadBrandKits=async ()=> {
    dispatch({type: 'SET_LOADING',payload: true});
    try {
      console.log('Loading brand kits from server...');
      if (state.isOnline) {
        const brandKits=await brandKitsApi.list();
        console.log('Loaded brand kits:',brandKits);
        dispatch({type: 'SET_BRAND_KITS',payload: normalizeBrandKits(brandKits)});
        saveToLocalStorage('brandKits',brandKits);
      } else {
        // Fallback to localStorage
        const localBrandKits=getFromLocalStorage('brandKits') || [];
        console.log('Using cached brand kits:',localBrandKits);
        dispatch({type: 'SET_BRAND_KITS',payload: normalizeBrandKits(localBrandKits)});
      }
    } catch (error) {
      console.error('Failed to load brand kits:',error);
      // Fallback to localStorage
      const localBrandKits=getFromLocalStorage('brandKits') || [];
      console.log('Error occurred,using cached brand kits:',localBrandKits);
      dispatch({type: 'SET_BRAND_KITS',payload: normalizeBrandKits(localBrandKits)});
      dispatch({type: 'SET_ERROR',payload: error.message});
    }
  };

  // Create brand kit
  const createBrandKit=async (brandKitData)=> {
    dispatch({type: 'SET_LOADING',payload: true});
    try {
      console.log('Creating brand kit with data:',brandKitData);
      let newBrandKit;
      
      if (state.isOnline) {
        newBrandKit=await brandKitsApi.create(brandKitData);
        console.log('Brand kit created on server:',newBrandKit);
        dispatch({type: 'ADD_BRAND_KIT',payload: newBrandKit});
      } else {
        // Save to localStorage only
        newBrandKit={
          id: `kit_${Date.now()}`,
          user_id: 'default_user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          ...brandKitData
        };
        console.log('Brand kit created locally:',newBrandKit);
        dispatch({type: 'ADD_BRAND_KIT',payload: newBrandKit});
      }
      
      // Update localStorage cache
      const updatedKits=[...state.brandKits,newBrandKit];
      saveToLocalStorage('brandKits',updatedKits);
      
      return newBrandKit;
    } catch (error) {
      console.error('Failed to create brand kit:',error);
      dispatch({type: 'SET_ERROR',payload: error.message});
      
      // Fallback to localStorage
      const newBrandKit={
        id: `kit_${Date.now()}`,
        user_id: 'default_user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...brandKitData
      };
      console.log('Brand kit created locally (fallback):',newBrandKit);
      dispatch({type: 'ADD_BRAND_KIT',payload: newBrandKit});
      const updatedKits=[...state.brandKits,newBrandKit];
      saveToLocalStorage('brandKits',updatedKits);
      
      return newBrandKit;
    }
  };

  // Update brand kit
  const updateBrandKit=async (id,updates)=> {
    dispatch({type: 'SET_LOADING',payload: true});
    try {
      console.log('Updating brand kit:',id,'with:',updates);
      let updatedBrandKit={
        ...state.brandKits.find(kit=> kit.id===id),
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      if (state.isOnline) {
        updatedBrandKit=await brandKitsApi.update(id,updatedBrandKit);
        console.log('Brand kit updated on server:',updatedBrandKit);
        dispatch({type: 'UPDATE_BRAND_KIT',payload: updatedBrandKit});
      } else {
        // Update localStorage only
        console.log('Brand kit updated locally:',updatedBrandKit);
        dispatch({type: 'UPDATE_BRAND_KIT',payload: updatedBrandKit});
      }
      
      // Update localStorage cache
      const updatedKits=state.brandKits.map(kit=> 
        kit.id===id ? updatedBrandKit : kit
      );
      saveToLocalStorage('brandKits',updatedKits);
      
      return updatedBrandKit;
    } catch (error) {
      console.error('Failed to update brand kit:',error);
      dispatch({type: 'SET_ERROR',payload: error.message});
      
      // Fallback to localStorage
      const updatedBrandKit={
        ...state.brandKits.find(kit=> kit.id===id),
        ...updates,
        updated_at: new Date().toISOString()
      };
      console.log('Brand kit updated locally (fallback):',updatedBrandKit);
      dispatch({type: 'UPDATE_BRAND_KIT',payload: updatedBrandKit});
      const updatedKits=state.brandKits.map(kit=> 
        kit.id===id ? updatedBrandKit : kit
      );
      saveToLocalStorage('brandKits',updatedKits);
      
      return updatedBrandKit;
    }
  };

  // Delete brand kit
  const deleteBrandKit=async (id)=> {
    dispatch({type: 'SET_LOADING',payload: true});
    try {
      console.log('Deleting brand kit:',id);
      if (state.isOnline) {
        await brandKitsApi.delete(id);
        console.log('Brand kit deleted on server');
      }
      
      dispatch({type: 'DELETE_BRAND_KIT',payload: id});
      
      // Update localStorage cache
      const updatedKits=state.brandKits.filter(kit=> kit.id !==id);
      saveToLocalStorage('brandKits',updatedKits);
    } catch (error) {
      console.error('Failed to delete brand kit:',error);
      dispatch({type: 'SET_ERROR',payload: error.message});
      
      // Fallback to localStorage
      dispatch({type: 'DELETE_BRAND_KIT',payload: id});
      const updatedKits=state.brandKits.filter(kit=> kit.id !==id);
      saveToLocalStorage('brandKits',updatedKits);
      
      throw error;
    }
  };

  // Set current brand kit
  const setCurrentBrandKit=(brandKit)=> {
    console.log('Setting current brand kit:',brandKit);
    dispatch({type: 'SET_CURRENT_BRAND_KIT',payload: brandKit});
    saveToLocalStorage('currentBrandKit',brandKit);
  };

  // Online/offline handling
  useEffect(()=> {
    const handleOnline=()=> {
      console.log('Coming online,syncing...');
      dispatch({type: 'SET_ONLINE',payload: true});
      loadBrandKits();// Sync when coming back online
    };
    const handleOffline=()=> {
      console.log('Going offline');
      dispatch({type: 'SET_ONLINE',payload: false});
    };

    window.addEventListener('online',handleOnline);
    window.addEventListener('offline',handleOffline);

    return ()=> {
      window.removeEventListener('online',handleOnline);
      window.removeEventListener('offline',handleOffline);
    };
  },[]);

  // Load initial data
  useEffect(()=> {
    loadBrandKits();
    
    // Load current brand kit from localStorage
    const savedCurrentKit=getFromLocalStorage('currentBrandKit');
    if (savedCurrentKit) {
      console.log('Loading saved current brand kit:',savedCurrentKit);
      dispatch({type: 'SET_CURRENT_BRAND_KIT',payload: savedCurrentKit});
    }
  },[]);

  const value={...state,createBrandKit,updateBrandKit,deleteBrandKit,setCurrentBrandKit,loadBrandKits};

  return (
    <BrandKitContext.Provider value={value}>
      {children}
    </BrandKitContext.Provider>
  );
}

export function useBrandKit() {
  const context=useContext(BrandKitContext);
  if (!context) {
    throw new Error('useBrandKit must be used within a BrandKitProvider');
  }
  return context;
}